import * as pdfjsLib from 'pdfjs-dist';

pdfjsLib.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjsLib.version}/build/pdf.worker.min.mjs`;

const MAX_OUTPUT_CHARS = 500_000;

function truncate(text: string): string {
  if (text.length <= MAX_OUTPUT_CHARS) return text;
  return text.slice(0, MAX_OUTPUT_CHARS) + `\n\n[... content truncated at ${MAX_OUTPUT_CHARS} characters ...]`;
}

export async function extractTextFromPDF(file: File): Promise<string> {
  try {
    const arrayBuffer = await file.arrayBuffer();
    const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;

    let fullText = '';
    for (let i = 1; i <= pdf.numPages; i += 1) {
      const page = await pdf.getPage(i);
      const textContent = await page.getTextContent();
      const pageText = textContent.items.map((item: any) => item.str).join(' ');
      fullText += `${pageText}\n\n`;
    }

    console.log(`Extracted ${fullText.length} characters from PDF (${pdf.numPages} pages)`);
    return truncate(fullText.trim());
  } catch (error) {
    console.error('PDF text extraction failed:', error);
    return '';
  }
}

export async function extractTextFromDOCX(file: File): Promise<string> {
  try {
    const arrayBuffer = await file.arrayBuffer();
    const mammoth = await import('mammoth/mammoth.browser');
    const result = await (mammoth as any).convertToHtml({ arrayBuffer });
    const html = String(result?.value ?? '');
    const tables: string[] = [];

    if (typeof DOMParser !== 'undefined') {
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      Array.from(parsed.querySelectorAll('table')).forEach((table, tableIndex) => {
        const rows = Array.from(table.querySelectorAll('tr'))
          .map((row) => Array.from(row.querySelectorAll('th,td'))
            .map((cell) => `"${(cell.textContent || '').replace(/\s+/g, ' ').trim().replace(/"/g, '""')}"`)
            .join(','))
          .filter((row) => row.trim().length > 0);
        if (rows.length > 0) {
          tables.push(`## Table: Table ${tableIndex + 1}\n${rows.join('\n')}`);
        }
      });
    }

    const text = html
      .replace(/<table[\s\S]*?<\/table>/gi, ' ')
      .replace(/<[^>]+>/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
    const output = [...tables, text].filter(Boolean).join('\n\n');
    console.log(`Extracted ${output.length} characters from DOCX: ${file.name}`);
    if (result?.messages?.length) {
      console.warn(`DOCX extraction warnings for ${file.name}:`, result.messages);
    }
    return truncate(output);
  } catch (error) {
    console.error('DOCX text extraction failed:', error);
    return `[Could not extract text from DOCX: ${file.name}]`;
  }
}

export async function extractTextFromSpreadsheet(file: File): Promise<string> {
  try {
    const arrayBuffer = await file.arrayBuffer();
    const XLSX = await import('xlsx');
    const workbook = XLSX.read(arrayBuffer, { type: 'array' });

    const parts: string[] = [];
    for (const sheetName of workbook.SheetNames) {
      const sheet = workbook.Sheets[sheetName];
      const csv = XLSX.utils.sheet_to_csv(sheet, { blankrows: false });
      if (!csv.trim()) continue;
      parts.push(`## Sheet: ${sheetName}\n${csv}`);
    }

    const output = parts.join('\n\n');
    console.log(`Extracted ${output.length} characters from spreadsheet (${workbook.SheetNames.length} sheets): ${file.name}`);
    return truncate(output);
  } catch (error) {
    console.error('Spreadsheet extraction failed:', error);
    return `[Could not extract text from spreadsheet: ${file.name}]`;
  }
}

export async function extractTextFromFile(file: File): Promise<string> {
  const fileType = file.type.toLowerCase();
  const fileName = file.name.toLowerCase();

  if (fileType === 'application/pdf' || fileName.endsWith('.pdf')) {
    return extractTextFromPDF(file);
  }

  if (fileType.startsWith('text/') || fileName.endsWith('.txt') || fileName.endsWith('.md')) {
    return truncate(await file.text());
  }

  if (
    fileType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
    fileName.endsWith('.docx')
  ) {
    return extractTextFromDOCX(file);
  }

  if (
    fileType === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
    fileType === 'application/vnd.ms-excel' ||
    fileType === 'text/csv' ||
    fileName.endsWith('.xlsx') ||
    fileName.endsWith('.xls') ||
    fileName.endsWith('.csv')
  ) {
    return extractTextFromSpreadsheet(file);
  }

  console.warn(`Unsupported file type for text extraction: ${fileType} (${fileName})`);
  return `[Document: ${file.name}]`;
}
